/**
 * CompanyInfoForm - Form for managing company information
 */

import type { CompanyInfoPublic, UpdateCompanyInfoRequest } from '@garden-platform/shared';
import type * as React from 'react';
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useCompanyInfo, useDeleteLogo, useUpdateCompanyInfo, useUploadLogo } from '../../hooks';

/** US States for dropdown */
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

interface FormData {
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZip: string;
  companyCountry: string;
  companyPhone: string;
  companyFax: string;
  companyEmail: string;
  companyWebsite: string;
  taxId: string;
  businessLicenseNumber: string;
}

const defaultFormData: FormData = {
  companyAddress: '',
  companyCity: '',
  companyState: '',
  companyZip: '',
  companyCountry: 'USA',
  companyPhone: '',
  companyFax: '',
  companyEmail: '',
  companyWebsite: '',
  taxId: '',
  businessLicenseNumber: '',
};

function mapCompanyInfoToFormData(info?: CompanyInfoPublic): FormData {
  if (!info) return defaultFormData;
  return {
    companyAddress: info.companyAddress || '',
    companyCity: info.companyCity || '',
    companyState: info.companyState || '',
    companyZip: info.companyZip || '',
    companyCountry: info.companyCountry || 'USA',
    companyPhone: info.companyPhone || '',
    companyFax: info.companyFax || '',
    companyEmail: info.companyEmail || '',
    companyWebsite: info.companyWebsite || '',
    taxId: info.taxId || '',
    businessLicenseNumber: info.businessLicenseNumber || '',
  };
}

export interface CompanyInfoFormProps {
  className?: string;
}

export const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({ className }) => {
  const { data: companyInfo, isLoading } = useCompanyInfo();
  const updateMutation = useUpdateCompanyInfo();
  const uploadLogoMutation = useUploadLogo();
  const deleteLogoMutation = useDeleteLogo();

  const [formData, setFormData] = useState<FormData>(() => mapCompanyInfoToFormData(companyInfo));
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Sync form data when company info loads
  const handleCompanyInfoLoaded = useCallback(() => {
    if (companyInfo && !isDirty) {
      setFormData(mapCompanyInfoToFormData(companyInfo));
    }
  }, [companyInfo, isDirty]);

  // Effect to sync data
  if (companyInfo && !isDirty && formData.companyAddress !== (companyInfo.companyAddress || '')) {
    handleCompanyInfoLoaded();
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (formData.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)) {
      newErrors.companyEmail = 'Invalid email format';
    }

    if (formData.companyZip && !/^\d{5}(-\d{4})?$/.test(formData.companyZip)) {
      newErrors.companyZip = 'Invalid ZIP code format';
    }

    if (formData.companyWebsite && !/^https?:\/\/.+/.test(formData.companyWebsite)) {
      newErrors.companyWebsite = 'Website must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const updateData: UpdateCompanyInfoRequest = {
      companyAddress: formData.companyAddress || null,
      companyCity: formData.companyCity || null,
      companyState: formData.companyState || null,
      companyZip: formData.companyZip || null,
      companyCountry: formData.companyCountry || null,
      companyPhone: formData.companyPhone || null,
      companyFax: formData.companyFax || null,
      companyEmail: formData.companyEmail || null,
      companyWebsite: formData.companyWebsite || null,
      taxId: formData.taxId || null,
      businessLicenseNumber: formData.businessLicenseNumber || null,
    };

    await updateMutation.mutateAsync(updateData);
    setIsDirty(false);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await uploadLogoMutation.mutateAsync(file);
  };

  const handleLogoDelete = async () => {
    await deleteLogoMutation.mutateAsync();
  };

  const getInputClassName = (field: keyof FormData) =>
    cn(errors[field] && 'border-red-500 focus:border-red-500 focus:ring-red-500');

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading company information...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Company Information</CardTitle>
        <CardDescription>
          Manage your organization&apos;s details for use on cheques and documents
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Logo Section */}
          <div className="space-y-3">
            <Label>Company Logo</Label>
            <div className="flex items-center gap-4">
              {companyInfo?.hasLogo ? (
                <div className="relative h-20 w-20 overflow-hidden rounded-lg border">
                  <img
                    src={`/api/organizations/logo?t=${Date.now()}`}
                    alt="Company logo"
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed bg-muted">
                  <span className="text-xs text-muted-foreground">No logo</span>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadLogoMutation.isPending}
                  className="w-auto"
                />
                {companyInfo?.hasLogo && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleLogoDelete}
                    disabled={deleteLogoMutation.isPending}
                  >
                    Remove Logo
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-1.5">
            <Label htmlFor="companyAddress">Street Address</Label>
            <Input
              id="companyAddress"
              type="text"
              value={formData.companyAddress}
              onChange={(e) => handleChange('companyAddress', e.target.value)}
              placeholder="123 Main Street"
              className={getInputClassName('companyAddress')}
            />
            {errors.companyAddress && (
              <p className="text-sm text-red-600">{errors.companyAddress}</p>
            )}
          </div>

          {/* City, State, ZIP */}
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3 space-y-1.5">
              <Label htmlFor="companyCity">City</Label>
              <Input
                id="companyCity"
                type="text"
                value={formData.companyCity}
                onChange={(e) => handleChange('companyCity', e.target.value)}
                placeholder="Denver"
                className={getInputClassName('companyCity')}
              />
              {errors.companyCity && <p className="text-sm text-red-600">{errors.companyCity}</p>}
            </div>

            <div className="col-span-1 space-y-1.5">
              <Label htmlFor="companyState">State</Label>
              <Select
                value={formData.companyState}
                onValueChange={(v) => handleChange('companyState', v)}
              >
                <SelectTrigger id="companyState" className={getInputClassName('companyState')}>
                  <SelectValue placeholder="--" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.companyState && <p className="text-sm text-red-600">{errors.companyState}</p>}
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="companyZip">ZIP Code</Label>
              <Input
                id="companyZip"
                type="text"
                value={formData.companyZip}
                onChange={(e) => handleChange('companyZip', e.target.value)}
                placeholder="80202"
                maxLength={10}
                className={getInputClassName('companyZip')}
              />
              {errors.companyZip && <p className="text-sm text-red-600">{errors.companyZip}</p>}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="companyPhone">Phone</Label>
              <Input
                id="companyPhone"
                type="tel"
                value={formData.companyPhone}
                onChange={(e) => handleChange('companyPhone', e.target.value)}
                placeholder="(555) 123-4567"
                className={getInputClassName('companyPhone')}
              />
              {errors.companyPhone && <p className="text-sm text-red-600">{errors.companyPhone}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="companyFax">Fax</Label>
              <Input
                id="companyFax"
                type="tel"
                value={formData.companyFax}
                onChange={(e) => handleChange('companyFax', e.target.value)}
                placeholder="(555) 123-4568"
                className={getInputClassName('companyFax')}
              />
              {errors.companyFax && <p className="text-sm text-red-600">{errors.companyFax}</p>}
            </div>
          </div>

          {/* Email and Website */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="companyEmail">Email</Label>
              <Input
                id="companyEmail"
                type="email"
                value={formData.companyEmail}
                onChange={(e) => handleChange('companyEmail', e.target.value)}
                placeholder="info@example.com"
                className={getInputClassName('companyEmail')}
              />
              {errors.companyEmail && <p className="text-sm text-red-600">{errors.companyEmail}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="companyWebsite">Website</Label>
              <Input
                id="companyWebsite"
                type="url"
                value={formData.companyWebsite}
                onChange={(e) => handleChange('companyWebsite', e.target.value)}
                placeholder="https://example.com"
                className={getInputClassName('companyWebsite')}
              />
              {errors.companyWebsite && (
                <p className="text-sm text-red-600">{errors.companyWebsite}</p>
              )}
            </div>
          </div>

          {/* Tax ID and License */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="taxId">Tax ID / EIN</Label>
              <Input
                id="taxId"
                type="text"
                value={formData.taxId}
                onChange={(e) => handleChange('taxId', e.target.value)}
                placeholder="XX-XXXXXXX"
                className={getInputClassName('taxId')}
              />
              {errors.taxId && <p className="text-sm text-red-600">{errors.taxId}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="businessLicenseNumber">Business License #</Label>
              <Input
                id="businessLicenseNumber"
                type="text"
                value={formData.businessLicenseNumber}
                onChange={(e) => handleChange('businessLicenseNumber', e.target.value)}
                placeholder="License number"
                className={getInputClassName('businessLicenseNumber')}
              />
              {errors.businessLicenseNumber && (
                <p className="text-sm text-red-600">{errors.businessLicenseNumber}</p>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFormData(mapCompanyInfoToFormData(companyInfo));
                setIsDirty(false);
                setErrors({});
              }}
              disabled={!isDirty}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending || !isDirty}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

CompanyInfoForm.displayName = 'CompanyInfoForm';
